import type { IQueueEvent } from "../../types";
import { eventTime } from "../../utils/eventTime";

function compareEvents(a: IQueueEvent, b: IQueueEvent): number {
  return eventTime(b) - eventTime(a);
}

function findMatchingIndexes(
  event: IQueueEvent,
  eventArray: IQueueEvent[],
): number[] {
  const eventContributingSet = new Set(event.contributing_events);
  return eventArray.reduce<number[]>((acc, arrayEvent, index) => {
    const arrayContributingSet = new Set(arrayEvent.contributing_events);
    const arr = Array.from(eventContributingSet);
    if (arr.some((e) => arrayContributingSet.has(e))) {
      acc.push(index);
    }
    return acc;
  }, []);
}

/**
 * Merges a new event into the existing event array
 * @param event - The new event to merge
 * @param existingEvents - The existing event array
 * @returns The updated event array
 */
export function mergeNewEvent(
  event: IQueueEvent,
  existingEvents: IQueueEvent[],
): IQueueEvent[] {
  // The noMetricEvent flag is true when an empty tagged event is added to the queue.
  if (eventTime(event) < 0 && !event.noMetricEvent) {
    // If the new event does not contain any data, abort
    return existingEvents;
  }
  const newEvents = existingEvents.slice();
  const matchingIndexes = findMatchingIndexes(event, newEvents);
  if (matchingIndexes.length <= 0) {
    // No match, so we add the new element
    newEvents.splice(0, 0, event);
    // The new event may not be the most temporally new (but it probably is), so we need to sort
    newEvents.sort(compareEvents);
    return newEvents;
  }

  // Newest events are kept at the front of the list
  let latestEvent = { serverTime: event.serverTime, event: event };
  // Find the first matching event with tag data
  const matchingEventWithTagData = matchingIndexes
    .map((index) => newEvents[index])
    .find((ev) => !!ev.tagData);

  matchingIndexes.forEach((index) => {
    const currentEvent = newEvents[index];
    if (currentEvent.serverTime! > latestEvent.serverTime!) {
      latestEvent = {
        serverTime: currentEvent.serverTime,
        event: currentEvent,
      };
    }
  });
  // Remove all the matching events except the first
  matchingIndexes
    .reverse()
    .slice(0, -1)
    .forEach((element) => {
      newEvents.splice(element, 1);
    });
  /* Insert the new event at the index of the first matching event
   * (if there is a matching event with tag data, that is merged together)
   */
  newEvents.splice(matchingIndexes[0], 1, {
    ...latestEvent.event,
    ...(matchingEventWithTagData
      ? {
          tagData: matchingEventWithTagData.tagData,
          gameState: matchingEventWithTagData.gameState,
          focusedPlayers: matchingEventWithTagData.focusedPlayers,
        }
      : {}),
  });
  // Sort the events
  newEvents.sort(compareEvents);

  return newEvents;
}
